import {
  Alert,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FormDialog } from '../../../shared/components/Dialogs/FormDialog';
import { ConfirmDialog } from '../../../shared/components/Dialogs/ConfirmDialog';
import { resources } from '../../../core/services/resources';
import { extractErrorMessage } from '../../../core/services/api';
import { ROLE_DESCRIPTION, ROLE_LABEL } from '../../../core/services/permissions';
import type { ProjectMember, ProjectRole } from '../../../core/types/data-models';

const ROLES: ProjectRole[] = ['OWNER', 'REVIEWER', 'VIEWER'];

interface ProjectMemberDialogProps {
  projectId: number;
  open: boolean;
  /** null = invite mode. Otherwise the member being edited. */
  member: ProjectMember | null;
  onClose: () => void;
}

/**
 * One dialog for both inviting a new person and editing an existing grant —
 * "same dialog" for both flows, per the header avatar-stack UX. Email is
 * locked in edit mode: it is the identity the grant is upserted on, so
 * changing it means removing and re-inviting rather than an in-place edit.
 */
export function ProjectMemberDialog({ projectId, open, member, onClose }: ProjectMemberDialogProps) {
  const isEdit = member !== null;
  const queryClient = useQueryClient();
  const membersApi = resources.projectMembersFor(projectId);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<ProjectRole>('VIEWER');
  const [expiresAt, setExpiresAt] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(member?.name ?? member?.invitedName ?? '');
    setEmail(member?.email ?? member?.invitedEmail ?? '');
    setRole(member?.role ?? 'VIEWER');
    setExpiresAt(member?.expiresAt ? member.expiresAt.slice(0, 10) : '');
    setError(null);
    setResendSuccess(false);
  }, [open, member]);

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['project-members', projectId] });

  const invite = useMutation({
    mutationFn: () =>
      resources.inviteProjectMember(projectId, {
        email: email.trim(),
        invitedName: name.trim() || undefined,
        role,
        expiresAt: expiresAt || null,
      }),
    onSuccess: () => {
      invalidate();
      onClose();
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  const update = useMutation({
    mutationFn: () =>
      membersApi.update(member!.id, {
        role,
        invitedName: name.trim() || null,
        expiresAt: expiresAt || null,
      } as Partial<ProjectMember>),
    onSuccess: () => {
      invalidate();
      onClose();
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  const remove = useMutation({
    mutationFn: () => membersApi.remove(member!.id),
    onSuccess: () => {
      setConfirmRemoveOpen(false);
      invalidate();
      onClose();
    },
    onError: (err) => {
      setConfirmRemoveOpen(false);
      setError(extractErrorMessage(err));
    },
  });

  const resend = useMutation({
    mutationFn: () => resources.resendProjectInvitation(projectId, member!.id),
    onSuccess: () => { setError(null); setResendSuccess(true); },
    onError: (err) => { setResendSuccess(false); setError(extractErrorMessage(err)); },
  });

  const isPending = invite.isPending || update.isPending || resend.isPending;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    if (isEdit) update.mutate();
    else invite.mutate();
  };

  return (
    <>
      <FormDialog
        open={open}
        title={isEdit ? 'Project access' : 'Invite to project'}
        submitLabel={isEdit ? 'Save' : 'Invite'}
        loading={isPending}
        onClose={onClose}
        onSubmit={onSubmit}
        startAction={
          isEdit ? (
            <>
              {member?.status === 'PENDING' && (
                <Button
                  variant="outlined"
                  disabled={isPending}
                  loading={resend.isPending}
                  onClick={() => resend.mutate()}
                >
                  Resend invitation
                </Button>
              )}
              <Button
                color="error"
                variant="outlined"
                disabled={isPending}
                onClick={() => setConfirmRemoveOpen(true)}
              >
                Remove access
              </Button>
            </>
          ) : undefined
        }
      >
        {error && <Alert severity="error">{error}</Alert>}
        {resendSuccess && <Alert severity="success">Invitation resent successfully.</Alert>}

        <TextField
          label="Name"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          helperText="Optional — shown until they accept the invitation."
        />

        <TextField
          label="Email"
          type="email"
          required
          fullWidth
          autoFocus={!isEdit}
          disabled={isEdit}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          helperText={
            isEdit
              ? "Can't be changed — remove access and invite again to use a different address."
              : "They'll get a link that only works for this address."
          }
        />

        <FormControl fullWidth>
          <InputLabel id="project-member-role-label">Permission</InputLabel>
          <Select
            labelId="project-member-role-label"
            label="Permission"
            value={role}
            onChange={(e) => setRole(e.target.value as ProjectRole)}
          >
            {ROLES.map((r) => (
              <MenuItem key={r} value={r}>
                <Tooltip title={ROLE_DESCRIPTION[r]} placement="right">
                  <span>{ROLE_LABEL[r]}</span>
                </Tooltip>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Expires"
          type="date"
          fullWidth
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          helperText="Leave blank for access that never expires."
        />
      </FormDialog>

      {isEdit && (
        <ConfirmDialog
          open={confirmRemoveOpen}
          title="Remove access?"
          message={`${member?.name ?? member?.invitedName ?? member?.email} will immediately lose access to this project.`}
          confirmLabel="Remove"
          destructive
          loading={remove.isPending}
          onClose={() => setConfirmRemoveOpen(false)}
          onConfirm={() => remove.mutate()}
        />
      )}
    </>
  );
}
